import React, { useState, useCallback, useEffect } from 'react';
import { FaBolt, FaPause, FaPlay, FaCog, FaSync, FaChartLine, FaShieldAlt, FaExclamationTriangle } from 'react-icons/fa';
import useSensexAlgo from '../../../hooks/useSensexAlgo';
import SignalCard from '../sensex/SignalCard';
import DailyPlanPanel from '../sensex/DailyPlanPanel';
import ModeSelector from '../sensex/ModeSelector';
import RegimeIndicator from '../sensex/RegimeIndicator';
import { useTrading } from '../../../context/TradingContext';

export default function SensexAlgoTab() {
  const [capital, setCapital] = useState(50000);
  const [mode, setMode] = useState('MANUAL');
  const [enabled, setEnabled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const { placeOrder, setAlgoOverlay } = useTrading();

  const {
    signals,
    regime,
    expectedMove,
    indicators,
    moveExhausted,
    scanning,
    error,
    lastScan,
    stats,
    marketOpen,
    dayType,
    moveUtilization,
    transition,
    timePhase,
    drawdown,
    protectionZone,
    scanMs,
    runScan,
    recordResult,
    resetDaily,
    fetchStats,
  } = useSensexAlgo({ capital, mode, enabled });

  // Publish algo overlay data to TradingContext for ProChart
  useEffect(() => {
    if (enabled && signals.length > 0) {
      const topSignal = signals.find(s => !s.blocked) || null;
      setAlgoOverlay({ signal: topSignal, expectedMove, regime });
    } else {
      setAlgoOverlay(null);
    }
    return () => setAlgoOverlay(null);
  }, [enabled, signals, expectedMove, regime, setAlgoOverlay]);

  const activeSignals = signals.filter(s => !s.blocked);
  const blockedSignals = signals.filter(s => s.blocked);

  // Execute a signal via existing order system
  const handleExecute = useCallback(async (signal) => {
    if (!signal || signal.blocked) return;

    try {
      await placeOrder({
        variety: 'NORMAL',
        tradingsymbol: signal.tradingsymbol,
        symboltoken: signal.symboltoken,
        transactiontype: 'BUY',
        exchange: 'BFO',
        ordertype: 'MARKET',
        producttype: 'INTRADAY',
        quantity: String(signal.quantity),
        price: '0',
        triggerprice: '0',
        duration: 'DAY',
      });
    } catch (err) {
      console.error('Order execution failed:', err);
    }
  }, [placeOrder]);

  return (
    <div className="space-y-4">
      {/* Top controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">SENSEX Algo Mind v2</h2>
          <p className="text-xs text-gray-500">
            30-module hybrid quant + adaptive + AI engine
            {scanMs ? <span className="text-gray-400 ml-2">({scanMs}ms)</span> : null}
            {!marketOpen && enabled && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">
                MARKET CLOSED — Last Session Data
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scanning && (
            <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-600 text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              Scanning...
            </span>
          )}
          {lastScan && (
            <span className="text-[10px] text-gray-400">
              Last: {lastScan.toLocaleTimeString()}
            </span>
          )}

          <button onClick={runScan} disabled={scanning}
            className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Manual scan">
            <FaSync className={`text-sm ${scanning ? 'animate-spin' : ''}`} />
          </button>

          <button onClick={() => setEnabled(!enabled)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              enabled ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
            }`}>
            {enabled ? <><FaPause className="text-xs" /> Stop</> : <><FaPlay className="text-xs" /> Start</>}
          </button>

          <button onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'}`}>
            <FaCog className="text-sm" />
          </button>

          <button onClick={fetchStats}
            className="p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Fetch performance stats">
            <FaChartLine className="text-sm" />
          </button>

          <button onClick={resetDaily}
            className="text-xs text-gray-400 hover:text-red-500 px-2 py-1" title="Reset daily state">
            Reset Day
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">{error}</div>
      )}

      {/* Protection zone warning */}
      {protectionZone && protectionZone.zone !== 'NORMAL' && (
        <div className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${
          protectionZone.zone === 'LOCKDOWN' ? 'bg-red-50 border-red-200 text-red-700' :
          protectionZone.zone === 'DEFENSIVE' ? 'bg-orange-50 border-orange-200 text-orange-700' :
          protectionZone.zone === 'CAUTIOUS' ? 'bg-amber-50 border-amber-200 text-amber-700' :
          'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          <FaShieldAlt />
          <span><strong>{protectionZone.zone}</strong>: {protectionZone.note}</span>
        </div>
      )}

      {/* Transition warning */}
      {transition?.transitioning && (
        <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-purple-700 text-sm flex items-center gap-2">
          <FaExclamationTriangle />
          <span>Regime transitioning: {transition.from_regime} → {transition.probable_next} ({transition.confidence}% confidence)</span>
        </div>
      )}

      {/* Mode selector */}
      {showSettings && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900">Algo Mode</h3>
          <ModeSelector mode={mode} onChange={setMode} />
        </div>
      )}

      {/* Status strip */}
      {enabled && (
        <div className="flex items-center gap-3 flex-wrap">
          {dayType?.day_type && (
            <StatusBadge label="Day Type" value={dayType.day_type} color={
              dayType.day_type === 'TREND' ? 'emerald' :
              dayType.day_type === 'CHOP' ? 'amber' :
              dayType.day_type === 'TRAP' ? 'red' : 'blue'
            } />
          )}
          {timePhase?.phase && (
            <StatusBadge label="Phase" value={timePhase.phase.replace(/_/g, ' ')} color="blue" />
          )}
          {moveUtilization?.zone && (
            <StatusBadge label="Move" value={`${moveUtilization.utilization_pct}% used`} color={
              moveUtilization.zone === 'EXHAUSTED' ? 'red' :
              moveUtilization.zone === 'LATE_MOVE' ? 'amber' : 'emerald'
            } />
          )}
          {drawdown?.zone && drawdown.zone !== 'GREEN' && (
            <StatusBadge label="DD Zone" value={`${drawdown.zone} (${drawdown.drawdown_pct}%)`} color={
              drawdown.zone === 'RED' ? 'red' : drawdown.zone === 'ORANGE' ? 'red' : 'amber'
            } />
          )}
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Regime + Plan + Stats */}
        <div className="space-y-4">
          <RegimeIndicator
            regime={regime}
            expectedMove={expectedMove}
            moveExhausted={moveExhausted}
            indicators={indicators}
          />
          <DailyPlanPanel capital={capital} onCapitalChange={setCapital} />

          {/* Drawdown panel */}
          {drawdown && drawdown.drawdown_pct > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FaShieldAlt className="text-blue-500" /> Capital Health
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <MiniStat label="Capital" value={`₹${drawdown.current_capital?.toLocaleString()}`} />
                <MiniStat label="Drawdown" value={`${drawdown.drawdown_pct}%`}
                  color={drawdown.drawdown_pct > 5 ? 'red' : drawdown.drawdown_pct > 3 ? 'amber' : 'gray'} />
                <MiniStat label="Win Streak" value={drawdown.win_streak || 0} color="emerald" />
                <MiniStat label="Loss Streak" value={drawdown.loss_streak || 0} color="red" />
              </div>
              {drawdown.recommendations?.length > 0 && (
                <div className="mt-2 text-[10px] text-gray-500">
                  {drawdown.recommendations[0]}
                </div>
              )}
            </div>
          )}

          {/* Stats panel */}
          {stats && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FaChartLine className="text-blue-500" /> Performance
              </h3>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {stats.adaptive && (
                  <>
                    <MiniStat label="Total Trades" value={stats.adaptive.total_trades || 0} />
                    <MiniStat label="Avg RR" value={`${stats.adaptive.avg_rr || 0}:1`} />
                  </>
                )}
                {stats.capital && (
                  <>
                    <MiniStat label="Growth" value={`${stats.capital.growth_pct || 0}%`}
                      color={(stats.capital.growth_pct || 0) >= 0 ? 'emerald' : 'red'} />
                    <MiniStat label="Daily P&L" value={`₹${stats.capital.daily_pnl || 0}`}
                      color={(stats.capital.daily_pnl || 0) >= 0 ? 'emerald' : 'red'} />
                  </>
                )}
                {stats.strategy_rotation?.strategies?.slice(0, 3).map(s => (
                  <React.Fragment key={s.name}>
                    <div className="col-span-2 text-gray-400 mt-1 flex items-center gap-1">
                      {s.name.replace(/_/g, ' ')}
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full ${
                        s.state === 'PEAK' ? 'bg-emerald-100 text-emerald-600' :
                        s.state === 'DECAYING' ? 'bg-red-100 text-red-600' :
                        s.state === 'DORMANT' ? 'bg-gray-100 text-gray-500' :
                        'bg-blue-100 text-blue-600'
                      }`}>{s.state}</span>
                    </div>
                    <MiniStat label="Win Rate" value={`${((s.metrics?.recent_win_rate || 0) * 100).toFixed(0)}%`}
                      color={(s.metrics?.recent_win_rate || 0) > 0.5 ? 'emerald' : 'red'} />
                    <MiniStat label="Weight" value={`${s.weight_recommendation}x`} />
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Center + Right: Signal cards */}
        <div className="lg:col-span-2 space-y-4">
          {/* Active signals */}
          {activeSignals.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FaBolt className="text-amber-500" />
                Active Signals ({activeSignals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {activeSignals.map((signal, i) => (
                  <SignalCard
                    key={`${signal.strategy}-${signal.strike}-${i}`}
                    signal={signal}
                    mode={mode}
                    onExecute={handleExecute}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No signals state */}
          {signals.length === 0 && !scanning && enabled && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <div className="text-4xl mb-3">📡</div>
              <div className="text-gray-500 text-sm">Scanning for signals...</div>
              <div className="text-gray-400 text-xs mt-1">
                {regime ? `Regime: ${regime.regime}` : 'Waiting for market data'}
                {dayType?.day_type ? ` | Day: ${dayType.day_type}` : ''}
              </div>
            </div>
          )}

          {!enabled && signals.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <div className="text-4xl mb-3">🎯</div>
              <div className="text-gray-900 font-medium">SENSEX Algo Engine v2.0</div>
              <div className="text-gray-500 text-sm mt-1">
                Click "Start" to begin scanning for trading signals
              </div>
              <div className="text-gray-400 text-xs mt-2">
                6 strategies | 30 modules | 4-layer pipeline | EQS gating | Monte Carlo
              </div>
            </div>
          )}

          {/* Blocked signals */}
          {blockedSignals.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Blocked Signals ({blockedSignals.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {blockedSignals.map((signal, i) => (
                  <SignalCard
                    key={`blocked-${signal.strategy}-${signal.strike}-${i}`}
                    signal={signal}
                    mode={mode}
                    onExecute={null}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ label, value, color = 'gray' }) {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-medium ${colors[color] || colors.gray}`}>
      <span className="text-gray-400">{label}:</span> {value}
    </span>
  );
}

function MiniStat({ label, value, color = 'gray' }) {
  const colorClass = {
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
    gray: 'text-gray-900',
  }[color] || 'text-gray-900';

  return (
    <div className="bg-gray-50 rounded p-2">
      <div className="text-[10px] text-gray-400 uppercase">{label}</div>
      <div className={`text-sm font-semibold ${colorClass}`}>{value}</div>
    </div>
  );
}
